import { BlobDto } from "../../app/blobs.types";
import { AddressDto, AuditedEntity, ConcurrencyHandledDto, PagedFilter } from "../../app/types";
import { BankAccountDetails, DiscountMode, InvoiceStatus } from "../invoicing/invoicing.types";
import { TenantUser } from "../users/users.types";

export enum TenantStatus {
    Active = 0,
    Billing_Lapsed = 3,
    Free_Trial = 5,
    Free_Plan = 10,
    Suspended = 20,
    Inactive = 30,
}

export enum SubscriptionPlan {
    FreeTrial = 0,
    Starter = 10,
    Growth = 20,
    GrowthPlus = 30,
    Enterprise = 40,
}

export interface TenantCreateDto extends ConcurrencyHandledDto {
    businessName: string;
    legalName?: string;
    natureOfBusiness: string;
    code: string;
    billingAddress?: AddressDto;
    bankAccount?: BankAccountDetails;
    phoneNumber?: string;
    email?: string;
    logo?: BlobDto;
    state: TenantStatus;
    stateEventDate?: string;
    notificationEmails?: string;
    referralCode?: string;
}

export interface Tenant extends TenantCreateDto, AuditedEntity {
    id: string;
    accountId: number;
    features: string[];
    users: TenantUser[];
}

export interface TenantCompactListItem {
    id: string;
    businessName: string;
    code: string;
    roles: string;
    state: TenantStatus;
    stateEventDate?: string;
}

export interface TenantFullListItem {
    id: string;
    accountId: number;
    businessName: string;
    legalName: string;
    lastTransactionTimeUtc?: string;
    code: string;
    transactionsCountInLast30Days: number;
    workLogCountInLast30Days: number;
    userCount: number;
    employeeCount: number;
    employeeCountWithSystemAccess: number;
    activeEmployeeCount: number;
    primaryContact: string;
    state: TenantStatus;
    stateEventDate?: string;
    subscriptionPrice: number;
}

export interface PermissionData {
    permission: string;
    roles: string[];
}

export interface FeaturePermissionData {
    feature: string;
    permissions: PermissionData[];
}

export interface RolePermissionData {
    roles: Record<string, string>;
    featurePermissions: FeaturePermissionData[];
}

export interface PackageSubscription {
    packageName: string;
    plan: SubscriptionPlan;
    unitPrice: number;
    discountMode: DiscountMode;
    discountValue: number;
    finalPrice: number;
}

export interface RateLimit {
    employeeLimit: number;
    transactionLimit: number;
    treeLimit: number;
    userLimit: number;
}

export interface TenantInvoiceGenerationResult {
    successCount: number;
    failureCount: number;
    skippedCount: number;
    errors: string[];
}

export interface TenantInvoice {
    id: string;
    accountId: number;
    tenantName: string;
    billingPeriodStart: string;
    billingPeriodEnd: string;
    invoiceNo: number;
    invoiceDate: string;
    dueDate: string;
    totalAmount: number;
    invoiceStatus: InvoiceStatus;
    invoicePdf: BlobDto;
    overDue: boolean;
    emailSent: boolean;
    referralBonusProcessed: boolean;
    referralBonus: number;
}

export interface TenantInvoiceFilter extends PagedFilter {
    searchTerm?: string;
    from?: string;
    to?: string;
    overdueOnly?: boolean;
}

export interface RevenueSummary {
    activeSubscriptionTotal: number;
    freeTrialSubscriptionTotal: number;
    activeTenantCount: number;
    freeTrialTenantCount: number;
}
